import HTMLTemplate from './template.html'
import './styles.scss'
import User from '../../modules/user'
import moment from 'moment'
import Chart from 'chart.js/auto'
import 'chartjs-adapter-moment'
import { CHART_OPTIONS } from './ChartOptions'
import { getFilteredAirports } from './../../services/AirportService'
import { filterInternationalSOSCityAndCountryAlerts, findIfAlertsAreInAirportRange } from '../../services/EventsService'
import { debounce, groupBy, sortBy } from 'lodash'
import { SETTINGS } from '../../SETTINGS'
import { InternationalSosEventDetailsPanel } from './../../components/InternationalSosEventDetailsPanel/InternationalSosEventDetailsPanel'
import { AviationAlertEventDetailsPanel } from './../../components/AviationAlertEventDetailsPanel/AviationAlertEventDetailsPanel'
import ClientPortalAPIService from './../../services/api/ClientPortal/ApiService'
import AvsecAlertsAPIService from './../../services/api/AvsecAlerts/ApiService'
import tippy, { followCursor } from 'tippy.js'
import Papa from 'papaparse'
import Sentry from './../../modules/SantryIO'
import { SideNavigation } from '../SideNavigation/SideNavigation'

VueDatePicker.props.enableTimePicker.default = false
VueDatePicker.props.showNowButton.default = true

let chartElement = null
const possibleChartColours = [
    'rgba(45, 58, 200, 1)',
    'rgba(243, 207, 87, 1)',
    'rgba(171, 39, 172, 1)',
    'rgba(233, 97, 94, 1)',
    'rgba(229, 52, 132, 1)',
    'rgba(238, 155, 76, 1)',
    'rgba(223, 253, 131, 1)'
]

export const Compare = (() => {
    var vueComponent = {
        components: { Datepicker: VueDatePicker },
        data() {
            return {
                showComponent: false,
                isCompareFilterAlerts: false,
                enableAlerts: true,
                enableAvsecAlerts: true,
                searchInput: '',
                searchResults: {
                    airports: []
                },
                isLoadingResults: false,
                isFetchingData: false,
                isFetchingEntryData: false,
                airportsData: [],
                airports: [],
                selectedAirports: [],
                selectedAirportsLimit: possibleChartColours.length,
                defaultColour: 'rgba(255,255,255, 0)',

                compareChartInitialised: false,
                compareChart: '',
                clearAirportsConfirmation: false,
                showSelectedAirportsLimit: false,

                allDateRangeAlerts: [],
                allDateRangeAviationAlerts: [],

                /* Date picker variables */
                datePickerConfiguration: {
                    showNowButton: true,
                    enableTimePicker: false
                },
                startDate: moment().subtract(1, 'month'),
                previousStartDate: new Date(),
                endDate: new Date(),
                minimumFilterDate: new Date('2016-01-01'),
                // Can't be in the future
                maximumFilterDate: new Date(),
                maximumGapRangeInDays: 365,
                alertFilters: {
                    Security: true,
                    Medical: true
                },
                alertSeverities: SETTINGS.INTERNATIONAL_SOS_SEVERITIES,
                displayEntryDetails: false,
                currentEntryDetails: {},
                isFirstDatasetEntry: false,
                isLastDatasetEntry: false,
                doSelectedAirportsHaveAnyEvents: true
            }
        },
        mounted() {
            this.startChart()
        },
        methods: {
            async initialize(airports) {
                this.airportsData = airports

                return await new Promise((resolve, reject) => {
                    this.isFetchingData = true

                    Promise.all([User.getLocalUserSettings(), User.getUserPermissions()]).then(values => {
                        // Get user saved airports for comparison 
                        const userComparisonSettings = JSON.parse(values[0].comstat)
                        if (userComparisonSettings.allAirports.length > 0) {
                            this.addAirports(userComparisonSettings.allAirports)
                        }

                        this.getEventsForDataRange().then(results => {
                            this.parseEventsServerResponse(results)
                            this.updateAirports()
                            this.updateChart()

                            userComparisonSettings.selectedAirports.forEach(airportICAO => this.selectAirport(airportICAO, ''))

                            this.isFetchingData = false

                            resolve(true)
                        }).catch(error => {
                            Sentry.captureException(error)
                            reject(error)
                        })

                        if (values[1].indexOf('medical') === -1) {
                            this.alertFilters.Medical = undefined
                        }
                    })
                })
            },
            parseEventsServerResponse(results) {
                if (results[0].status === 'fulfilled') {
                    this.allDateRangeAlerts = results[0].value || []
                } else {
                    console.error('There was an error fetching alerts, please try again by selecting a different data range.')
                    this.allDateRangeAlerts = []
                }

                if (results[1].status === 'fulfilled') {
                    this.allDateRangeAviationAlerts = results[1].value || []
                } else {
                    console.error('There was an error fetching alerts, please try again by selecting a different data range.')
                    this.allDateRangeAviationAlerts = []
                }
            },
            show() {
                this.showComponent = true
                this.updateUserAirportCodePreferences().then(() => {
                    this.updateAirports()
                    this.updateChart()
                })

                this.hideEntryDetails()
            },
            hide() {
                this.showComponent = false
                SideNavigation.$data.activeModule = ''
            },
            async getEventsForDataRange() {
                return await new Promise((resolve, reject) => {
                    const startDate = this.startDate.toISOString()
                    const endDate = this.endDate.toISOString()
                    let alertCountryCodes = []
                    this.airports.forEach(airport => {
                        const airportDetails = this.getAirportDetails(airport.ICAO)
                        alertCountryCodes.push(airportDetails.Isocode3)
                    })

                    const eventPromises = [
                        ClientPortalAPIService.getInternationalSOSAlertsLimited(startDate, endDate, alertCountryCodes.join(',')),
                        AvsecAlertsAPIService.getAlerts(startDate, endDate)
                    ]

                    Promise.allSettled(eventPromises).then(results => {
                        resolve(results)
                    }).catch(error => {
                        Sentry.captureException(error)
                        reject(error)
                    })
                })
            },
            handleDateUpdate() {
                // This is called whenever a start date or end date is selected
                this.hideEntryDetails()
                this.fetchEventsForSelections()
            },
            fetchEventsForSelections() {
                // Filter changes or date changes will trigger this function, but if there are no airports added there is no need to
                // a call APIs which wouldn't return anything anyway
                if (this.airports.length === 0) return

                let startLoadingTime = performance.now()
                this.isFetchingData = true

                this.getEventsForDataRange().then(results => {
                    this.parseEventsServerResponse(results)
                    this.updateAirports()
                    this.updateChart()
                })
                    .catch(error => Sentry.captureException(error))
                    .finally(() => {
                        let interval = window.setInterval(() => {
                            if (performance.now() - startLoadingTime > 1000) {
                                this.isFetchingData = false
                                window.clearInterval(interval)
                            }
                        }, 100)
                    })
            },
            getAirportDetails(ICAOCode) {
                return this.airportsData.find(airportData => airportData.ICAO === ICAOCode)
            },
            getAirportAlerts(ISO_3166_1_ALPHA_3, latitude, longitude) {
                if (!this.enableAlerts) return []

                // Transoform coordinates to numbers
                const coordinates = [latitude, longitude].map(coordinate => Number(coordinate))
                const filteredAlertsByLocation = filterInternationalSOSCityAndCountryAlerts(this.allDateRangeAlerts, coordinates, ISO_3166_1_ALPHA_3)
                return filteredAlertsByLocation.filter(alert => {
                    return this.alertFilters[alert.Service] === true && this.alertSeverities[alert.Severity] === true
                })
            },
            getAirportAviationAlerts(ISO_3166_1_ALPHA_3, ICAOCode, latitude, longitude) {
                if (!this.enableAvsecAlerts || !this.enableAlerts) return []
                let aviationAlerts = []
                this.allDateRangeAviationAlerts.forEach(aviationAlert => {
                    if (aviationAlert?.AffectedAirports?.includes(ICAOCode)) {
                        aviationAlerts.push(aviationAlert)
                        return
                    }
                    let countriesAffected = aviationAlert?.CountriesAffected?.some(affectedCountry => affectedCountry.CountryCodeAlpha3 === ISO_3166_1_ALPHA_3)
                    if (countriesAffected.Name) {
                        aviationAlerts.push(aviationAlert)
                        return
                    }
                    const cords = aviationAlert?.AffectedLocation?.Coordinates || []
                    if (cords.length > 0) {
                        if (cords?.[0] === 0 && cords?.[1] === 0) {
                            return
                        }
                        let distanceFromAirport = findIfAlertsAreInAirportRange(aviationAlert?.AffectedLocation, [latitude, longitude]) || null
                        if (distanceFromAirport && distanceFromAirport <= 50) {
                            aviationAlerts.push(aviationAlert)
                            return
                        }
                    }
                })
                return aviationAlerts
            },
            updateAirports() {
                this.airports.forEach(airport => {
                    const airportDetails = this.getAirportDetails(airport.ICAO)
                    airport.alerts = this.getAirportAlerts(airportDetails.Isocode3, airportDetails.Lat, airportDetails.Lng)
                    airport.aviationAlerts = this.getAirportAviationAlerts(airportDetails.Isocode3, airport.ICAO, airportDetails.Lat, airportDetails.Lng)
                })

                this.selectedAirports.forEach(airport => {
                    const airportDetails = this.getAirportDetails(airport.ICAO)
                    airport.alerts = this.getAirportAlerts(airportDetails.Isocode3, airportDetails.Lat, airportDetails.Lng)
                    airport.aviationAlerts = this.getAirportAviationAlerts(airportDetails.Isocode3, airport.ICAO, airportDetails.Lat, airportDetails.Lng)
                })
            },
            async updateUserAirportCodePreferences() {
                return await new Promise((resolve, reject) => {
                    User.getLocalUserSettings().then(settings => {
                        this.airports.forEach(airport => {
                            airport.airportCode = airport[settings.airportCode]

                            // Some airports don't have IATA code, in this case we are going to display the airport's ICAO code
                            if (!airport.airportCode) airport.airportCode = airport.ICAO
                        })

                        this.selectedAirports.forEach(airport => {
                            airport.airportCode = airport[settings.airportCode]
                            if (!airport.airportCode) airport.airportCode = airport.ICAO
                        })

                        resolve(true)
                    }).catch(error => {
                        Sentry.captureException(error)
                        reject(error)
                    })
                })
            },
            _addAirport(ICAOCode) {
                if (this.airports.findIndex(airport => airport.ICAO === ICAOCode) === -1) {
                    const airportDetails = this.getAirportDetails(ICAOCode)
                    this.airports.unshift({
                        ICAO: ICAOCode,
                        IATA: airportDetails.IATA,
                        airportCity: airportDetails.City,
                        airportCode: ICAOCode, // ICAO or IATA based on user preferences, initialized with ICAO code
                        name: airportDetails.Name,
                        alerts: this.getAirportAlerts(airportDetails.Isocode3, airportDetails.Lat, airportDetails.Lng),
                        aviationAlerts: this.getAirportAviationAlerts(airportDetails.Isocode3, ICAOCode, airportDetails.Lat, airportDetails.Lng),
                        colour: this.defaultColour,
                        selected: false
                    })
                }
            },
            addAirport(ICAOCode, fetchLatestEvents) {
                this._addAirport(ICAOCode)
                this.updateUserAirportCodePreferences()
                this.searchInput = ''

                if (fetchLatestEvents === undefined || fetchLatestEvents === true) {
                    this.fetchEventsForSelections()
                }

                this.debounceUpdateUserSettings()
            },
            addAirports(airportsICAOCodes) {
                airportsICAOCodes.forEach(ICAOCode => this.addAirport(ICAOCode, false))
                this.fetchEventsForSelections()
                this.debounceUpdateUserSettings()
            },
            _removeAirport(ICAOCode) {
                const airportIndex = this.airports.findIndex(airport => airport.ICAO === ICAOCode)
                this.airports.splice(airportIndex, 1)

                // If the airport was selected, then remove it as well from the selected airports array
                const selectedAirportIndex = this.selectedAirports.findIndex(airport => airport.ICAO === ICAOCode)
                if (selectedAirportIndex > -1) {
                    this.selectedAirports.splice(selectedAirportIndex, 1)
                    this.hideTooltip()
                    this.hideEntryDetails()
                    this.updateChart()
                }
            },
            removeAirport(ICAOCode) {
                this._removeAirport(ICAOCode)
                this.debounceUpdateUserSettings()
            },
            removeAllAirports() {
                const airportsToRemove = this.airports.map(airport => airport.ICAO)
                airportsToRemove.forEach(airportICAO => this._removeAirport(airportICAO))
            },
            clearAllAirports() {
                this.removeAllAirports()
                this.clearAirportsConfirmation = false
                this.debounceUpdateUserSettings()
            },
            selectAirport(ICAOCode, type) {
                if (this.selectedAirports.length >= this.selectedAirportsLimit && type === 'compare') {
                    this.showSelectedAirportsLimit = true
                    return
                }

                let airportIndex = this.airports.findIndex(airport => airport.ICAO === ICAOCode)
                let alreadySelectedAirportColours = this.selectedAirports.map(airport => airport.colour)
                let availableColour = possibleChartColours.find(colour => alreadySelectedAirportColours.indexOf(colour) === -1)

                this.airports[airportIndex].selected = true
                this.airports[airportIndex].colour = availableColour

                //If the airport is already selected, we don't want to add it again
                if (!this.selectedAirports.some(airport => airport.ICAO === ICAOCode)) {
                    this.selectedAirports.push(this.airports[airportIndex])
                } 

                this.updateChart()
                this.debounceUpdateUserSettings()
            },
            unselectAirport(ICAOCode) {
                let airportIndex = this.airports.findIndex(airport => airport.ICAO === ICAOCode)
                let selectedAirportIndex = this.selectedAirports.findIndex(airport => airport.ICAO === ICAOCode)

                this.airports[airportIndex].selected = false
                this.airports[airportIndex].colour = this.defaultColour

                this.selectedAirports.splice(selectedAirportIndex, 1)

                if (this.currentEntryDetails.ICAO === ICAOCode) {
                    this.hideEntryDetails()
                }

                this.hideTooltip()
                this.updateChart()
                this.debounceUpdateUserSettings()
            },
            toggleAirportSelection(ICAOCode) {
                if (this.selectedAirports.some(airport => airport.ICAO === ICAOCode)) {
                    this.unselectAirport(ICAOCode)
                } else {
                    this.selectAirport(ICAOCode, 'compare')
                }
            },
            updateChart() {
                chartElement.data.datasets = []
                this.selectedAirports.forEach(airport => {
                    let format = 'YYYY-MM-DD'
                    // If the date range is big we will groups events by month and year
                    let monthsDifference = moment.duration(moment(this.endDate).diff(this.startDate)).asMonths()
                    let dateGroupsAsMonths = false
                    if (monthsDifference > 6) {
                        format = 'YYYY-MM'
                        dateGroupsAsMonths = true
                    }

                    const groupedAlertsByDate = groupBy(airport.alerts, alert => moment(alert.CreatedDate).startOf('day').format(format))
                    const groupedAviationAlertsByDate = groupBy(airport.aviationAlerts, alert => moment(alert.Date).startOf('day').format(format))

                    let dataOfTheDataset = []
                    // Now we loop alerts and update eventually existing dataset or create a new entry
                    for (const key in groupedAlertsByDate) {
                        const alertsInDate = groupedAlertsByDate[key]
                        const existingDataSetIndex = dataOfTheDataset.findIndex(data => data.x === key)

                        if (existingDataSetIndex > -1) {
                            dataOfTheDataset[existingDataSetIndex].alerts = alertsInDate
                        } else {
                            dataOfTheDataset.push({
                                x: key,
                                y: 0,
                                alerts: alertsInDate,
                                aviationAlerts: []
                            })
                        }
                    }

                    // Now we loop Aviation alerts and update eventually existing dataset or create a new entry
                    for (const key in groupedAviationAlertsByDate) {
                        const aviationAlertsInDate = groupedAviationAlertsByDate[key]
                        const existingDataSetIndex = dataOfTheDataset.findIndex(data => data.x === key)

                        if (existingDataSetIndex > -1) {
                            dataOfTheDataset[existingDataSetIndex].aviationAlerts = aviationAlertsInDate
                        } else {
                            dataOfTheDataset.push({
                                x: key,
                                y: 0,
                                alerts: [],
                                aviationAlerts: aviationAlertsInDate
                            })
                        }
                    }

                    // Update all datasets `y` value for all combined length of all events
                    dataOfTheDataset.forEach(data => data.y = data.alerts.length + data.aviationAlerts.length)

                    // Sort all entries by the date
                    dataOfTheDataset = sortBy(dataOfTheDataset, ['x'])

                    chartElement.data.datasets.push({
                        label: airport.airportCode,
                        borderColor: airport.colour,
                        enableAlerts: this.enableAlerts,
                        enableAvsecAlerts: this.enableAvsecAlerts,
                        airportCity: airport.airportCity,
                        airportCode: airport.airportCode,
                        ICAOCode: airport.ICAO,
                        dateGroupsAsMonths: dateGroupsAsMonths,
                        data: dataOfTheDataset
                    })
                })

                chartElement.data.datasets.forEach(currentDataset => {
                    // We want to know that as the airport willl currently overlap with any other airports that come from the same city
                    const overlappingAirports = chartElement.data.datasets.filter(existingDataset => {
                        return existingDataset.ICAOCode !== currentDataset.ICAOCode && currentDataset.airportCity === existingDataset.airportCity
                    })

                    if (overlappingAirports.length > 0) {
                        const alreadyHandledOverlappingAirports = chartElement.data.datasets.filter(existingDataset => {
                            return existingDataset.ICAOCode !== currentDataset.ICAOCode && currentDataset.airportCity === existingDataset.airportCity && existingDataset.borderDash !== undefined
                        })

                        if (alreadyHandledOverlappingAirports.length === 0) {
                            currentDataset.borderDash = [20]
                            currentDataset.borderJoinStyle = 'round'
                        } else {
                            currentDataset.borderDash = [20]
                            currentDataset.borderDashOffset = 20
                            currentDataset.borderJoinStyle = 'round'
                        }
                    }
                })

                // After we have all datasets we find the lowest entry and the highest entry and for the airports that
                // don't have the lowest and highest entry we create one, so graphs for some airports won't be cut
                let lowestDatasetDate = '9999-99-99'
                let highestDatasetDate = ''
                chartElement.data.datasets.forEach(dataset => {
                    dataset.data.forEach(entry => {
                        // The `x` value format is date format, so simple comparison still works fine
                        if (entry.x < lowestDatasetDate) lowestDatasetDate = entry.x
                        if (entry.x > highestDatasetDate) highestDatasetDate = entry.x
                    })
                })

                chartElement.data.datasets.forEach(dataset => {
                    let isAlreadyLowestDateEntry = dataset.data.findIndex(entry => entry.x === lowestDatasetDate) > -1
                    let isAlreadyHighestDateEntry = dataset.data.findIndex(entry => entry.x === highestDatasetDate) > -1

                    if (!isAlreadyLowestDateEntry) {
                        dataset.data.unshift({
                            x: lowestDatasetDate,
                            y: 0,
                            alerts: [],
                            aviationAlerts: []
                        })
                    }

                    if (!isAlreadyHighestDateEntry) {
                        dataset.data.push({
                            x: highestDatasetDate,
                            y: 0,
                            alerts: [],
                            aviationAlerts: []
                        })
                    }
                })

                this.doSelectedAirportsHaveAnyEvents = chartElement.data.datasets.some(dataset => {
                    return dataset.data.some(entry => entry.alerts.length > 0 || entry.aviationAlerts.length > 0)
                })

                chartElement.update('none')
            },
            triggerTooltip(datasetIndex, dataIndex) {
                const tooltip = chartElement.tooltip
                const chartArea = chartElement.chartArea

                this.hideTooltip()

                tooltip.setActiveElements([{
                    datasetIndex: datasetIndex,
                    index: dataIndex
                }],
                    {
                        x: (chartArea.left + chartArea.right) / 2,
                        y: (chartArea.top + chartArea.bottom) / 2
                    })

                chartElement.update('none')
            },
            hideTooltip() {
                const tooltip = chartElement.tooltip
                if (tooltip.getActiveElements().length > 0) {
                    tooltip.setActiveElements([], { x: 0, y: 0 })
                }
            },
            openAirportInfo(airportData) {
                if (!airportData.selected) {
                    this.selectAirport(airportData.ICAO, 'compare')
                }

                // Call chartElement.getDatasetMeta(0) passing Dataset index for more Canvas display information

                const datasetIndex = chartElement.data.datasets.findIndex(dataset => dataset.ICAOCode === airportData.ICAO)
                const dataset = chartElement.data.datasets[datasetIndex]

                // So when the user clicks on the info button for the airport the intention was to open a dataset of the airport
                // with all events in that date period, but we didn't know which dataset would be the best to open
                // so for now I thought that opening the dataset with most event in it would be the best choice
                let dataIndexWithMostEvents = null
                let mostEventsFound = 0
                for (let index = 0; index < dataset.data.length; index++) {
                    const dataEntry = dataset.data[index]
                    const totalDataEntryEvents = dataEntry.alerts.length + dataEntry.aviationAlerts.length

                    if (totalDataEntryEvents > mostEventsFound) {
                        mostEventsFound = totalDataEntryEvents
                        dataIndexWithMostEvents = index
                    }
                }

                this.triggerTooltip(datasetIndex, dataIndexWithMostEvents)
                this.showEntryDetails(airportData.ICAO, dataset.data[dataIndexWithMostEvents].x)
            },
            startChart() {
                const ctx = document.getElementById('myChart').getContext('2d')
                chartElement = new Chart(ctx, {
                    type: 'line',
                    data: {
                        datasets: []
                    },
                    options: CHART_OPTIONS
                })

                this.compareChartInitialised = true
                this.compareChart = chartElement
            },
            debounceInput: debounce(function () {
                this.search()
            }, 500),
            search() {
                const filteredAirports = getFilteredAirports(this.searchInput, this.airportsData)

                // Add a UI helper property to style airports that are already added to compare slightly differently in search results.
                const alreadySelectedAirportCodes = this.airports.map(airport => airport.ICAO)
                filteredAirports.forEach(airport => airport.compare = alreadySelectedAirportCodes === -1)

                this.searchResults.airports = filteredAirports
                this.isLoadingResults = false
            },
            debounceUpdateUserSettings: debounce(function () {
                this.updateUserSettings()
            }, 1000),
            updateUserSettings() {
                User.getLocalUserSettings().then(settings => {
                    let updatedUserSettings = settings
                    updatedUserSettings.comstat = JSON.stringify({
                        ...JSON.parse(settings.comstat),
                        allAirports: this.airports.map(airport => airport.ICAO).reverse(),
                        selectedAirports: this.selectedAirports.map(airport => airport.ICAO),
                        startDate: '',
                        endDate: ''
                    })

                    User.updateUserSettings(updatedUserSettings)
                })
            },
            resetAlertFilters() {
                this.enableAvsecAlerts = true

                for (const key in this.alertFilters) {
                    this.alertFilters[key] = true
                }

                for (const key in this.alertSeverities) {
                    this.alertSeverities[key] = true
                }
            },
            updateEventFilters() {
                this.isCompareFilterAlerts = false
                this.updateAirports()
                this.updateChart()
                this.updateElementsAfterFilterChanges()
            },
            updateElementsAfterFilterChanges() {
                this.hideEntryDetails()
                // I feel like it's probably better to just hide the date, instead of checking for filters and 
                // Updating the selection
                // if (this.displayEntryDetails = true) {
                //     this.showEntryDetails(this.currentEntryDetails.ICAO, this.currentEntryDetails.x)
                // }
            },
            highlightDatasetPoint(dataset, datasetIndex, datasetDataIndex) {
                // Dynamically change the style of all points in the dataset and style currently selected point
                // With Chart JS this seems the way to achieve it
                let datasetPointColours = []
                let datasetPointRadiuses = []
                let datasetPointBorderColours = []
                for (let index = 0; index < dataset.data.length; index++) {
                    datasetPointColours[index] = 'rgba(19, 19, 24, 1)'
                    datasetPointRadiuses[index] = 3
                    datasetPointBorderColours[index] = dataset.borderColor
                    if (index === datasetDataIndex) {
                        datasetPointColours[index] = dataset.borderColor
                        datasetPointRadiuses[index] = 6
                        datasetPointBorderColours[index] = 'white'
                    }
                }

                chartElement.config.data.datasets[datasetIndex]['pointBackgroundColor'] = datasetPointColours
                chartElement.config.data.datasets[datasetIndex]['pointRadius'] = datasetPointRadiuses
                chartElement.config.data.datasets[datasetIndex]['pointBorderColor'] = datasetPointBorderColours
            },
            unhighlightDatasetPoints() {
                for (let index = 0; index < chartElement.data.datasets.length; index++) {
                    const dataset = chartElement.data.datasets[index]

                    let datasetPointColours = []
                    let datasetPointRadiuses = []
                    let datasetPointBorderColours = []
                    for (let index = 0; index < dataset.data.length; index++) {
                        datasetPointColours[index] = 'rgba(19, 19, 24, 1)'
                        datasetPointRadiuses[index] = 3
                        datasetPointBorderColours[index] = dataset.borderColor
                    }

                    chartElement.config.data.datasets[index]['pointBackgroundColor'] = datasetPointColours
                    chartElement.config.data.datasets[index]['pointRadius'] = datasetPointRadiuses
                    chartElement.config.data.datasets[index]['pointBorderColor'] = datasetPointBorderColours
                }
            },
            showEntryDetails(ICAO, date) {
                const datasetIndex = chartElement.data.datasets.findIndex(dataset => dataset.ICAOCode === ICAO)
                const dataset = chartElement.data.datasets[datasetIndex]
                const datasetDataIndex = dataset.data.findIndex(data => data.x === date)
                const datasetData = dataset.data[datasetDataIndex]
                const dateFormat = dataset.dateGroupsAsMonths ? 'MMMM' : 'll'

                this.unhighlightDatasetPoints()
                this.highlightDatasetPoint(dataset, datasetIndex, datasetDataIndex)

                this.isFirstDatasetEntry = datasetDataIndex === 0
                this.isLastDatasetEntry = dataset.data.length === datasetDataIndex + 1

                this.displayEntryDetails = true

                this.currentEntryDetails = {
                    x: date,
                    airportCode: dataset.airportCode,
                    ICAO: dataset.ICAOCode,
                    colour: dataset.borderColor,
                    date: moment(datasetData.x).format(dateFormat),
                    alerts: [],
                    aviationAlerts: datasetData.aviationAlerts,
                    orderedEvents: []
                }

                const alertIDs = datasetData.alerts.map(alert => alert.Id)
                if (datasetData.alerts.length > 0) {
                    this.isFetchingEntryData = true
                    chartElement.update('none')
                    let startLoadingTime = performance.now()
                    ClientPortalAPIService.getInternationalSOSAlertsByIDs(alertIDs).then(results => {
                        this.currentEntryDetails.alerts = results

                        this.combineCurrentEntryDatePeriodEvents()

                        // Put a minimum 1 second loading screen to avoid annoying flickering if the server response is too fast
                        let interval = window.setInterval(() => {
                            if (performance.now() - startLoadingTime > 1000) {
                                this.isFetchingEntryData = false
                                chartElement.update('none')
                                window.clearInterval(interval)
                            }
                        }, 100)
                    })
                    .catch(error => {
                        let errorMessage = 'There was an error fetching alert details for the selected period. If the issue persists, please contact the administrator of the platform.'
                        window.alert(errorMessage)
                        console.error(errorMessage, error)
                    })
                } else {
                    this.combineCurrentEntryDatePeriodEvents()
                }

                // We can't have animation as it won't trigger the resize of the chart if the user doesn't move his mouse
                chartElement.update('none')
            },
            previousEntry() {
                const datasetIndex = chartElement.data.datasets.findIndex(dataset => dataset.ICAOCode === this.currentEntryDetails.ICAO)
                const dataset = chartElement.data.datasets[datasetIndex]
                const datasetDataIndex = dataset.data.findIndex(data => data.x === this.currentEntryDetails.x) - 1
                const datasetData = dataset.data[datasetDataIndex]

                this.showEntryDetails(this.currentEntryDetails.ICAO, datasetData.x)
                this.triggerTooltip(datasetIndex, datasetDataIndex)
            },
            nextEntry() {
                const datasetIndex = chartElement.data.datasets.findIndex(dataset => dataset.ICAOCode === this.currentEntryDetails.ICAO)
                const dataset = chartElement.data.datasets[datasetIndex]
                const datasetDataIndex = dataset.data.findIndex(data => data.x === this.currentEntryDetails.x) + 1
                const datasetData = dataset.data[datasetDataIndex]

                this.showEntryDetails(this.currentEntryDetails.ICAO, datasetData.x)
                this.triggerTooltip(datasetIndex, datasetDataIndex)
            },
            combineCurrentEntryDatePeriodEvents() {
                // We have separated arrays with different event types, now we want to combine all of them into one chronogically
                // ordered array, so it's easier to display them
                let eventsToAdd = []
                this.currentEntryDetails.alerts.forEach(alert => {
                    let alertDate = alert.CreatedDate
                    let alertLocationName = ''
                    if (alert.Locations.features[0].properties.Name !== alert.Locations.features[0].properties.CountryNameList) {
                        alertLocationName = alert.Locations.features[0].properties.Name + ', ' + alert.Locations.features[0].properties.CountryNameList
                    } else {
                        alertLocationName = alert.TitleLocation
                    }

                    eventsToAdd.push({
                        id: alert.Id,
                        isInternationalSOSAlert: true,
                        date: alertDate ? moment.utc(alertDate).format() : '',	
                        displayDate: alertDate ? moment.utc(alertDate).format('DD MMM YYYY') : '',
                        time: alertDate ? moment.utc(alertDate).format('HH:mm') + ' UTC' : '',
                        location: alertLocationName,
                        description: alert.Title,
                        categories: alert.Categories,
                        status: alert.Status
                    })
                })

                this.currentEntryDetails.aviationAlerts.forEach(alert => {
                    eventsToAdd.push({
                        id: alert.NodeId,
                        isAvsecAlert: true,
                        date: moment.utc(alert.Date).format(),
                        displayDate: moment.utc(alert.Date).format('DD MMM YYYY'),
                        time: moment.utc(alert.Date).format('HH:mm') + ' UTC',
                        location: alert.SelectedCountry,
                        description: alert.Heading.replace(/(<([^>]+)>)/ig, '')
                    })
                })

                // Sort events, newest are first in the array
                this.currentEntryDetails.orderedEvents = sortBy(eventsToAdd, ['date']).reverse()
            },
            hideEntryDetails() {
                this.unhighlightDatasetPoints()
                this.hideTooltip()
                this.displayEntryDetails = false
                chartElement.update('none')
            },
            openEvent(event) {
                let eventDetails = null
                if (event.isInternationalSOSAlert) {
                    eventDetails = this.currentEntryDetails.alerts.find(alert => alert.Id === event.id)
                    InternationalSosEventDetailsPanel.showAlert(eventDetails)
                } else if (event.isAvsecAlert) {
                    eventDetails = this.currentEntryDetails.aviationAlerts.filter(alert => alert.NodeId === event.id)
                    AviationAlertEventDetailsPanel.showAvsecAlerts(eventDetails)
                }
            },
            exportComparisonData() {
                let airportDataArray = []
                const eventStartDate = moment(this.startDate).format('DD-MMM-YY')
                const eventEndDate = moment(this.endDate).format('DD-MMM-YY')
                const todaysDate = moment(new Date()).format('MM-DD-YY')

                this.airports.forEach(res => {
                    const totalSecurityAlerts = _.size(_.filter(res.alerts, { 'Service': 'Security' }))
                    const totalMedicalAlerts = _.size(_.filter(res.alerts, { 'Service': 'Medical' }))
                    const aviationAlerts = _.size(_.flatMap(res.aviationAlerts))
                    let airportDataObject = {
                        'ICAO Code': res.ICAO,
                        'IATA Code': res.IATA,
                        'Airport Name': res.name,
                        'Airport City': res.airportCity,
                        'Security Alert Count': '-',
                        'Medical Alert Count': '-',
                        'Aviation Alert Count': '-'
                    }

                    if (this.enableAlerts) {
                        if (this.alertFilters.Security) {
                            airportDataObject['Security Alert Count'] = totalSecurityAlerts
                        }
                        if (this.alertFilters.Medical) {
                            airportDataObject['Medical Alert Count'] = totalMedicalAlerts
                        }
                    }

                    if (this.enableAlerts && this.enableAvsecAlerts) {
                        airportDataObject['Aviation Alert Count'] = aviationAlerts
                    }

                    airportDataArray.push(airportDataObject)
                })

                const parsedAirportDataArray = Papa.unparse(airportDataArray)
                const blob = new Blob([`Event Start Date, ${eventStartDate},,Event End Date, ${eventEndDate}\n\n${parsedAirportDataArray}`], { type: 'text/csv;charset=utf-8;' })

                const downloadLink = document.createElement('a')
                downloadLink.href = URL.createObjectURL(blob)
                downloadLink.download = `Airport Comparison ${todaysDate}.csv`

                downloadLink.click()
            },
            checkIfAletIsEnabled() {
                if (this.alertFilters.Security || this.alertFilters.Medical || this.enableAvsecAlerts) {
                    this.enableAlerts = true
                } else {
                    this.enableAlerts = false
                }
            }
        },
        watch: {
            searchInput() {
                this.isLoadingResults = this.searchInput.trim() !== ''
                this.debounceInput()
            },
            enableAlerts() {
                this.updateAirports()
                this.updateChart()
                this.updateElementsAfterFilterChanges()
            }
        },
        template: HTMLTemplate
    }

    const DOMElement = document.getElementById('vue-compare')
    if (DOMElement === null) {
        return null
    } else {
        return Vue.createApp(vueComponent).use({
            install: (app) => {
                app.directive('tooltip', {
                    beforeMount(el, binding) {
                        tippy(el, {
                            content: binding.value,
                            placement: 'bottom-start',
                            animation: 'shift-away',
                            allowHTML: true,
                            trigger: 'mouseenter click focus',
                            arrow: false,
                            followCursor: true,
                            plugins: [followCursor]
                        })
                    }
                })
            }
        }).mount(DOMElement)
    }
})();